import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Award, Globe, ChevronRight, ArrowRight } from 'lucide-react';
import { stats } from '@/data/fundsData';

export const HeroSection = ({ onNavigate }) => {
  return (
    <section className="relative overflow-hidden bg-primary">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      
      {/* Gold Accent Line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-accent" />
      
      <div className="container relative mx-auto px-4 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <Badge 
              variant="outline" 
              className="border-accent/40 text-accent bg-accent/10 font-sans text-xs uppercase tracking-wider px-3 py-1"
            >
              <Award className="h-3 w-3 mr-1.5" />
              Trusted Since 2022
            </Badge>
            
            <div className="space-y-4">
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground leading-tight">
                The Best Place to Find the{' '}
                <span className="text-accent">Best Funds</span>
              </h1>
              <p className="text-lg text-primary-foreground/80 max-w-xl leading-relaxed font-sans">
                Comprehensive annual investment rankings and expert analysis to guide your investment decisions across global markets.
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg"
                className="btn-premium bg-accent text-accent-foreground hover:bg-accent/90 font-sans font-medium"
                onClick={() => onNavigate('rankings')}
              >
                Explore Rankings
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10 font-sans font-medium"
                onClick={() => onNavigate('about')}
              >
                Learn More
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center gap-6 pt-4">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-success animate-pulse" />
                <span className="text-sm text-primary-foreground/70">Updated for 2025</span>
              </div>
              <div className="h-4 w-px bg-primary-foreground/20" />
              <span className="text-sm text-primary-foreground/70">Independent Research</span>
            </div>
          </div>

          {/* Right Content - Stats Cards */}
          <div className="grid grid-cols-2 gap-4">
            <div className="stat-card">
              <div className="relative z-10">
                <Globe className="h-8 w-8 text-accent mb-3" />
                <p className="text-3xl lg:text-4xl font-serif font-bold text-primary-foreground">
                  {stats.countriesCovered}+
                </p>
                <p className="text-sm text-primary-foreground/70 font-sans mt-1">
                  Countries Covered
                </p>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="relative z-10">
                <TrendingUp className="h-8 w-8 text-accent mb-3" />
                <p className="text-3xl lg:text-4xl font-serif font-bold text-primary-foreground">
                  {stats.totalFunds.toLocaleString()}+
                </p>
                <p className="text-sm text-primary-foreground/70 font-sans mt-1">
                  Funds Analyzed
                </p>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="relative z-10">
                <Award className="h-8 w-8 text-accent mb-3" />
                <p className="text-3xl lg:text-4xl font-serif font-bold text-primary-foreground">
                  {stats.yearsOfData}
                </p>
                <p className="text-sm text-primary-foreground/70 font-sans mt-1">
                  Years of Data
                </p>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="relative z-10">
                <div className="h-8 w-8 rounded-full bg-accent/20 flex items-center justify-center mb-3">
                  <span className="text-accent font-bold text-sm">$</span>
                </div>
                <p className="text-3xl lg:text-4xl font-serif font-bold text-primary-foreground">
                  {stats.totalAUM}
                </p>
                <p className="text-sm text-primary-foreground/70 font-sans mt-1">
                  Total AUM Tracked
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 60V30C240 10 480 0 720 10C960 20 1200 50 1440 40V60H0Z" fill="hsl(var(--background))" />
        </svg>
      </div>
    </section>
  );
};
